import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Box,
    Typography,
    Checkbox,
    FormControlLabel,
    IconButton,
    Collapse,
    Paper,
    Button,
} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTheme } from "@mui/material";
import { tokens } from "../theme";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import InterpreterModeOutlinedIcon from '@mui/icons-material/InterpreterModeOutlined';
import "./AdminSetting.css"

export default function AdminSetting() {
    const { roleId } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [sidebars, setSidebars] = useState([]);
    const [expanded, setExpanded] = useState({});
    const [permissions, setPermissions] = useState({});
    const [sidebarChecks, setSidebarChecks] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [currentDate, setCurrentDate] = useState(getFormattedDate());



    useEffect(() => {
        async function fetchPermissions() {
            setIsLoading(true);
            try {
                const res = await fetch(`/api/sidebars/role/${roleId}/`);
                if (!res.ok) {
                    throw new Error(`HTTP error! Status: ${res.status}`);
                }
                const data = await res.json();
                const sidebarData = Array.isArray(data) ? data : [];
                setSidebars(sidebarData);

                const initialPermissions = {};
                const initialSidebarChecks = {};
                sidebarData.forEach((sidebar) => {
                    if (sidebar.roles?.[0]?.permissions) {
                        const perms = sidebar.roles[0].permissions;
                        Object.entries(perms).forEach(([perm, value]) => {
                            initialPermissions[`${perm}_${sidebar.sidebar_id}`] = value;
                        });
                        const allChecked = Object.values(perms).every((val) => val);
                        const anyChecked = Object.values(perms).some((val) => val);
                        initialSidebarChecks[sidebar.sidebar_id] = allChecked
                            ? true
                            : anyChecked
                                ? "indeterminate"
                                : false;
                    } else {
                        initialSidebarChecks[sidebar.sidebar_id] = false;
                    }
                });
                setPermissions(initialPermissions);
                setSidebarChecks(initialSidebarChecks);
            } catch (error) {
                console.error("Error fetching permissions:", error);
                toast.error(`Failed to fetch permissions: ${error.message}`);
            } finally {
                setIsLoading(false);
            }
        }
        fetchPermissions();
    }, [roleId]);



    const handleExpandClick = useCallback((sidebarId) => {
        setExpanded((prev) => ({
            ...prev,
            [sidebarId]: !prev[sidebarId],
        }));
    }, []);

    const handleSidebarCheckboxChange = useCallback(
        (sidebarId) => (event) => {
            const isChecked = event.target.checked;
            setSidebarChecks((prev) => ({
                ...prev,
                [sidebarId]: isChecked,
            }));

            setPermissions((prev) => {
                const updatedPermissions = { ...prev };
                const sidebar = sidebars.find((s) => s.sidebar_id === sidebarId);
                if (sidebar?.roles?.[0]?.permissions) {
                    Object.keys(sidebar.roles[0].permissions).forEach((perm) => {
                        updatedPermissions[`${perm}_${sidebarId}`] = isChecked;
                    });
                }
                return updatedPermissions;
            });
        },
        [sidebars]
    );

    const handleCheckboxChange = useCallback(
        (sidebarId, perm) => (event) => {
            const isChecked = event.target.checked;
            setPermissions((prev) => {
                const updatedPermissions = {
                    ...prev,
                    [`${perm}_${sidebarId}`]: isChecked,
                };

                const sidebar = sidebars.find((s) => s.sidebar_id === sidebarId);
                if (sidebar?.roles?.[0]?.permissions) {
                    const allPermissions = Object.keys(sidebar.roles[0].permissions);
                    const allChecked = allPermissions.every(
                        (p) => p === perm ? isChecked : updatedPermissions[`${p}_${sidebarId}`]
                    );
                    const anyChecked = allPermissions.some(
                        (p) => p === perm ? isChecked : updatedPermissions[`${p}_${sidebarId}`]
                    );
                    setSidebarChecks((prevChecks) => ({
                        ...prevChecks,
                        [sidebarId]: allChecked ? true : anyChecked ? "indeterminate" : false,
                    }));
                }
                return updatedPermissions;
            });
        },
        [sidebars]
    );

    const handleSaveChanges = async () => {
        setIsLoading(true);
        try {
            const payload = sidebars.map((sidebar) => ({
                sidebar_id: sidebar.sidebar_id,
                is_add: permissions[`is_add_${sidebar.sidebar_id}`] || false,
                is_view: permissions[`is_view_${sidebar.sidebar_id}`] || false,
                is_update: permissions[`is_update_${sidebar.sidebar_id}`] || false,
                is_delete: permissions[`is_delete_${sidebar.sidebar_id}`] || false,
            }));

            const response = await fetch(`/api/user-permission/${roleId}/`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || `HTTP error! Status: ${response.status}`);
            }

            toast.success("Permissions updated successfully");
        } catch (error) {
            console.error("Error saving permissions:", error);
            toast.error(error.message || "Failed to save permissions");
        } finally {
            setIsLoading(false);
        }
    };


    function getFormattedDate() {
        const now = new Date();
        const day = now.toLocaleString("en-US", { weekday: "short" });
        const month = now.toLocaleString("en-US", { month: "short" });
        const date = now.toLocaleString("en-US", { day: "2-digit" });
        const year = now.getFullYear();
        const time = now.toLocaleString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        });
        return `${day}, ${month} ${date}, ${year} ${time}`;
    }

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentDate(getFormattedDate());
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    return (
        <Box className="container">
            <ToastContainer />
            <div className="header">
                <Box className="header-box">
                    <InterpreterModeOutlinedIcon className="icon" />
                    <span className="title">Role Permissions</span>
                </Box>
                <span className="current-date">{currentDate}</span>
            </div>

            <Box className="button-container">
                <Box className="button-group">
                    <Button
                        variant="contained"
                        onClick={() => navigate("/AdminPage")}
                        disabled={isLoading}
                        startIcon={<ArrowBackIcon />}
                        className="back-button"
                    >
                        Back
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveChanges}
                        disabled={isLoading}
                        startIcon={<SaveIcon />}
                        className="save-button"
                    >
                        Save
                    </Button>
                </Box>
            </Box>
            {isLoading ? (
                <Typography variant="h6">Loading...</Typography>
            ) : (
                <Paper className="paper">
                    {sidebars.length === 0 ? (
                        <Typography variant="subtitle1">No sidebars available</Typography>
                    ) : (
                        sidebars.map((sidebar) => (
                            <Paper key={sidebar.sidebar_id} className="sidebar-paper">
                                <Box className="sidebar-header">
                                    <Box className="checkbox-container">
                                        <Checkbox
                                            checked={sidebarChecks[sidebar.sidebar_id] === true}
                                            indeterminate={sidebarChecks[sidebar.sidebar_id] === "indeterminate"}
                                            className="checkbox"
                                        />
                                        <Typography className="title" variant="h6">{sidebar.sidebar_name}</Typography>
                                    </Box>
                                    <IconButton
                                        onClick={() => handleExpandClick(sidebar.sidebar_id)}
                                        aria-expanded={expanded[sidebar.sidebar_id] || false}
                                        aria-label="show permissions"
                                    >
                                        <ExpandMoreIcon />
                                    </IconButton>
                                </Box>
                                <Collapse in={expanded[sidebar.sidebar_id] || false} timeout="auto" unmountOnExit>
                                    <Box className="permissions-container">
                                        {sidebar.roles?.[0]?.permissions ? (
                                            <>
                                                <Typography variant="subtitle1" gutterBottom>
                                                    Permissions:
                                                </Typography>
                                                {Object.entries(sidebar.roles[0].permissions).map(([perm]) => (
                                                    <FormControlLabel
                                                        key={perm}
                                                        className="permission-label"
                                                        control={
                                                            <Checkbox
                                                                checked={permissions[`${perm}_${sidebar.sidebar_id}`] || false}
                                                                onChange={handleCheckboxChange(sidebar.sidebar_id, perm)}
                                                            />
                                                        }
                                                        label={perm.replace("is_", "").replace("_", " ").toUpperCase()}
                                                    />
                                                ))}
                                            </>
                                        ) : (
                                            <Typography variant="subtitle2">No permissions available</Typography>
                                        )}
                                    </Box>
                                </Collapse>
                            </Paper>
                        ))
                    )}
                </Paper>
            )}
        </Box>
    );
}